import i18n from '@/plugins/i18n'
import { title } from '@/utilities/filters'
import { addId } from '@/utilities/functions'
import { removeCategorizerBlankReferences } from '@/utilities/bruteForce'
import { FILTER_VALUES } from '@/utilities/constants'
import orderBy from 'lodash/orderBy'

const { AGE_RANGE, GENDER, STUDY_STATUS } = FILTER_VALUES

const ageRangeFilter = context => {
  const getAriaLabel = s => s.split('-').join('to')
  const ageRange = context.$store.getters['search/facets']?.ageRange || {}
  const { ADULT, ADOLESCENT, CHILD, INFANT } = AGE_RANGE
  const filter = {
    heading: { name: title(i18n.t('org.ageRange')) },
    name: 'ageRange',
    options: addId([
      {
        ariaLabel: getAriaLabel(ADULT),
        count: ageRange.adult18,
        text: ADULT,
        value: 'adult',
        title: 'Age Range',
      },
      {
        ariaLabel: getAriaLabel(ADOLESCENT),
        count: ageRange.adolescent1317,
        text: ADOLESCENT,
        value: 'adolescent',
        title: 'Age Range',
      },
      {
        ariaLabel: getAriaLabel(CHILD),
        count: ageRange.child312,
        text: CHILD,
        value: 'child',
        title: 'Age Range',
      },
      {
        ariaLabel: getAriaLabel(INFANT),
        count: ageRange.infant02Years,
        name: 'Infant 0 - 2 years',
        text: INFANT,
        value: 'infant',
        title: 'Age Range',
      },
    ]).map(o => ({ ...o, active: false })),
  }
  return filter
}

const distanceFilter = context => {
  const locationSelected = !!context.location
  const { dist } = context.$store.getters['search/params']
  const counts = locationSelected ? context.$store.getters['search/distanceCounts'] : {}
  const filter = {
    heading: { name: i18n.t('org.distance') },
    message: locationSelected ? '' : 'Please specify a location to select a distance',
    name: 'distance',
    radio: true,
    options: addId([
      { count: counts.five, text: '5 miles', value: 5, title: 'Distance' },
      { count: counts.ten, text: '10 miles', value: 10, title: 'Distance' },
      { count: counts.twenty, text: '20 miles', value: 20, title: 'Distance' },
      { count: counts.uk, text: 'All locations', value: 1000, title: 'Distance' },
    ]).map(o => ({ ...o, active: false, disabled: !locationSelected })),
  }
  const activeFilter = filter.options.find(o => o.value === dist)
  if (activeFilter) activeFilter.active = true
  return filter
}

export const filterItems = context =>
  addId([
    healthConditionFilter(context),
    distanceFilter(context),
    ageRangeFilter(context),
    genderFilter(context),
    studyStatusFilter(context),
  ])

const genderFilter = context => {
  const gender = context.$store.getters['search/facets']?.gender || {}
  const { FEMALE, MALE } = GENDER
  const filter = {
    heading: { name: i18n.t('org.gender') },
    name: 'gender',
    options: addId([
      { ariaLabel: FEMALE, count: gender.female, text: FEMALE, value: 'female', title: 'Gender' },
      { ariaLabel: MALE, count: gender.male, text: MALE, value: 'male', title: 'Gender' },
      {
        ariaLabel: context.$t('org.allGenders'),
        count: gender.allGenders,
        text: context.$t('org.allGenders'),
        value: 'notSpecified',
        title: 'Gender',
      },
    ]).map(o => ({ ...o, active: false })),
  }
  return filter
}

const healthConditionFilter = context => {
  const healthCategoryAndTags = context.$store.getters['search/facets']?.healthCategoryAndTags || []
  const unsorted = removeCategorizerBlankReferences(healthCategoryAndTags)
  let sorted
  const sortAlphabetically = shouldSortCategoriesAlphabetically(context)
  if (sortAlphabetically) sorted = orderBy(unsorted, [c => c.name.toLowerCase()], ['asc'])
  const categories = sorted || unsorted
  return {
    categories,
    heading: { name: title(i18n.t('org.healthCondition')) },
    name: 'condition',
    message: categories?.length ? '' : context.$t('org.noFiltersAvailableForThisSelection'),
  }
}

const shouldSortCategoriesAlphabetically = context => {
  const query = context.$route.query.query
  const locationString = context.$route.query.location
  const hasQuery = query && typeof query === 'string' && query != '*'
  const hasLocationString = locationString && typeof locationString === 'string'
  if (hasLocationString && !hasQuery) return true
  else if (!hasLocationString && !hasQuery) return true
  else return false
}

const studyStatusFilter = context => {
  const studyStatus = context.$store.getters['search/facets']?.studyStatus || {}
  const { COMPLETED, NOT_RECRUITING, RECRUITING, STOPPED } = STUDY_STATUS
  const filter = {
    heading: { name: title(i18n.t('org.studyStatus')) },
    name: 'studyStatus',
    options: addId([
      {
        ariaLabel: RECRUITING,
        count: studyStatus.recruiting,
        text: RECRUITING,
        value: 'recruiting',
        title: 'Study status',
      },
      {
        ariaLabel: COMPLETED,
        count: studyStatus.completed,
        text: COMPLETED,
        value: 'completed',
        title: 'Study status',
      },
      {
        ariaLabel: NOT_RECRUITING,
        count: studyStatus.notRecruiting,
        text: NOT_RECRUITING,
        value: 'notRecruiting',
        title: 'Study status',
      },
      {
        ariaLabel: STOPPED,
        count: studyStatus.stopped,
        text: STOPPED,
        value: 'stopped',
        title: 'Study status',
      },
    ]).map(o => ({ ...o, active: false })),
  }
  const params = context.$store.getters['search/params']
  filter.options.forEach(o => {
    if (params?.facetDef['Study Status']?.includes(o.text)) o.active = true
  })
  return filter
}
